import type { UseMutationResult } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import { errorStatus } from "app/pages/sales-intelligence/utils/errors/errorStatus";
import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import { SiError, errorCategoryMap } from "app/pages/sales-intelligence/utils/errors/types";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import { OPTIMISTIC_DEAL_SIGNAL_ID } from "../../constants";
import signalRecommenderTrackingService from "../../services/tracking";
import type {
    SignalRecommenderFileDto,
    SignalRecommenderFilesResponseDto,
    SignalRecommenderUploadResponseDto,
} from "../../types/dto";
import type { DealSignalUploadInput } from "../../types/model";
import { signalRecommenderFileStatusMap } from "../../types/model";
import { signalRecommenderQueryKeys } from "../key-factory/factory";

type UploadContext = { readonly previous: SignalRecommenderFilesResponseDto | undefined };

const UPLOAD_RETRY_DELAY_MS = 1000;
const MAX_UPLOAD_RETRIES = 1;
const REQUEST_TIMEOUT_STATUS = 408;
const SERVER_ERROR_STATUS_FLOOR = 500;

const countFilterValues = (filters: Record<string, readonly string[]> | null | undefined): number =>
    filters ? Object.values(filters).reduce((sum, values) => sum + values.length, 0) : 0;

const buildUploadBody = ({ file, signalFilters }: DealSignalUploadInput): FormData => {
    const body = new FormData();

    body.append("file", file);

    if (signalFilters) {
        body.append("signalFilters", JSON.stringify(signalFilters));
    }

    return body;
};

/**
 * Anything that is not a status we raised ourselves is a `fetch` rejection — the request never reached
 * the server, so a second attempt cannot duplicate a run. 429 is deliberately absent: it reports the
 * upload cooldown, which a one-second wait cannot clear.
 */
const isRetriableUploadError = (error: Error): boolean => {
    const status = errorStatus(error);

    return is.undefined(status) || status === REQUEST_TIMEOUT_STATUS || status >= SERVER_ERROR_STATUS_FLOOR;
};

const shouldRetryUpload = (failureCount: number, error: Error): boolean =>
    failureCount < MAX_UPLOAD_RETRIES && isRetriableUploadError(error);

/**
 * The row an in-flight upload stands in for. Only the name, the timestamp and the status are ever read:
 * `DealSignalFileItem` gives an actively-processing run its own layout, with no metrics and no action.
 *
 * `created` carries a time of day because both the stall test and the elapsed label bail without one —
 * a date alone would parse as midnight and the row would claim to be hours old the moment it appeared.
 */
const buildOptimisticRow = (fileName: string): SignalRecommenderFileDto => ({
    id: OPTIMISTIC_DEAL_SIGNAL_ID,
    enrichmentTypeName: "Signals",
    fileName,
    created: new Date().toISOString(),
    companiesCount: 0,
    foundSignalsCount: 0,
    status: signalRecommenderFileStatusMap.inProgress,
    isDownloaded: false,
});

export const useUploadDealSignalFileMutation = (): UseMutationResult<
    SignalRecommenderUploadResponseDto,
    Error,
    DealSignalUploadInput,
    UploadContext
> => {
    const queryClient = useQueryClient();

    const filesKey = signalRecommenderQueryKeys.files();

    return useMutation<SignalRecommenderUploadResponseDto, Error, DealSignalUploadInput, UploadContext>({
        /**
         * The multipart seam hands back the raw `Response`, so the status check belongs here: `fetch`
         * resolves on a 500, and without it an error body would parse as a successful upload. The status
         * rides along on the error because `shouldRetryUpload` is the only thing that can read it back.
         */
        async mutationFn(input) {
            const response = await salesApiService.signalRecommender.uploadFile(buildUploadBody(input));

            if (!response.ok) {
                throw new SiError(`Deal signal upload failed with status ${response.status}`, {
                    category: errorCategoryMap.api,
                    operation: "uploadDealSignalFile",
                    status: response.status,
                });
            }

            return response.json() as Promise<SignalRecommenderUploadResponseDto>;
        },

        // One silent retry stands in for the reader: the modal closes on submit, so recovering from a
        // transient failure by hand costs them the whole flow again — find the file, re-pick the filters,
        // re-confirm. The callbacks below still run once per submit, not once per attempt.
        retry: shouldRetryUpload,
        retryDelay: UPLOAD_RETRY_DELAY_MS,

        // The modal closes on submit, so the list is where the reader lands — and an empty history would
        // otherwise answer "no enrichments yet" to the file they just handed over. The placeholder also
        // starts the poll, since the interval predicate reads these same rows.
        async onMutate({ file, signalFilters }) {
            signalRecommenderTrackingService.trackEnrichmentSubmit(
                countFilterValues(signalFilters?.intent),
                countFilterValues(signalFilters?.technology),
            );

            await queryClient.cancelQueries({ queryKey: filesKey });

            const previous = queryClient.getQueryData<SignalRecommenderFilesResponseDto>(filesKey);

            queryClient.setQueryData<SignalRecommenderFilesResponseDto>(filesKey, {
                nextUploadAllowedAt: previous?.nextUploadAllowedAt ?? null,
                enrichments: [buildOptimisticRow(file.name), ...(previous?.enrichments ?? [])],
            });

            return { previous };
        },

        // Swapping the placeholder for the server's own row keeps the id stable across the refetch that
        // follows; letting it change would remount the row and restart its progress animation.
        onSuccess({ request, acceptedRowsCount, skippedRowsCount }) {
            signalRecommenderTrackingService.trackEnrichmentUploaded(acceptedRowsCount, skippedRowsCount);

            queryClient.setQueryData<SignalRecommenderFilesResponseDto>(filesKey, (current) =>
                current
                    ? {
                          ...current,
                          enrichments: current.enrichments.map((file) =>
                              file.id === OPTIMISTIC_DEAL_SIGNAL_ID ? request : file,
                          ),
                      }
                    : current,
            );
        },

        // Rolling the placeholder back is load-bearing rather than tidy: the poll runs for as long as a
        // row reads as in progress, so one left behind would poll forever against a job that never began.
        //
        // No toast: this layer reports the failure, and `useDealSignalEnrichment` decides how to show it.
        onError(error, _input, context) {
            queryClient.setQueryData(filesKey, context?.previous);

            signalRecommenderTrackingService.trackEnrichmentUploadFailed();

            trackError(
                new SiError(
                    "Failed to upload the deal signals file",
                    { category: errorCategoryMap.api, operation: "uploadDealSignalFile" },
                    { cause: error },
                ),
            );
        },

        // A non-ok response does not prove the server created nothing, and the list is the only place the
        // reader can find out — so it is resynchronised on either outcome, not only on success.
        onSettled() {
            void queryClient.invalidateQueries({ queryKey: filesKey });
        },
    });
};
